<script lang="ts" module>
	export const NO_VALUE = 'no_value';

	export interface SelectChangeEvent extends CustomEvent {
		detail: {
			value: string;
			selectedOption: SelectOption;
		};
	}

	export interface SelectOption {
		label?: string;
		value?: string | undefined | null;
		image?: { url: string; width: number; height: number; alt: string };
	}

	export interface SelectProps {
		size?: 'sm' | 'md';
		labelVariant?: TxtVariant | undefined;
		disabled?: boolean;
		label?: string | undefined;
		required?: boolean | undefined;
		optional?: boolean | undefined;
		error?: string | undefined;
		name: string;
		value?: string | undefined | null;
		options: SelectOption[];
		searchable?: boolean;
		searchPlaceholder?: string | undefined;
		placeholder?: string | undefined;
		ariaLabel: string;
		selectedPrefix?: string | undefined;
		onblur?: (e?: FocusEvent) => void;
		onchange?: (e: SelectChangeEvent) => void;
		dataCy: string;
		tooltip?: string | undefined;
		showIcon?: boolean;
		class?: string;
		children?: import('svelte').Snippet;
		icon?: import('svelte').Snippet;
		optionSlot?: import('svelte').Snippet<[SelectOption]>;
		selectedOptionSlot?: import('svelte').Snippet<[SelectOption | undefined]>;
		classes?: {
			button?: string;
			label?: string;
			placeholder?: string;
			option?: string;
			tooltip?: string;
			tooltipClasses?: { tooltipClasses?: { content?: string } };
			// A function form receives the menu's open direction so callers can vary classes
			// (e.g. reverse a drop shadow when the menu opens above the trigger).
			menu?: string | ((ctx: { openedAbove: boolean }) => string);
			helper?: string;
		};
	}
</script>

<script lang="ts">
	import classNames from 'classnames';
	import { fade } from 'svelte/transition';
	import { twMerge } from 'tailwind-merge';
	import { ChevronDownIcon } from '../../assets/icons/ChevronDownIcon';
	import { clickOutside } from '../../utils/clickOutside/clickOutside';
	import { isIosDevice } from '../../utils/isIosDevice/isIosDevice';
	import { InfoTooltip } from '../InfoTooltip';
	import { InputLabel } from '../InputLabel';
	import type { TxtVariant } from '../Txt';
	import { Txt } from '../Txt';

	let {
		size = 'md',
		labelVariant = 'label3',
		disabled = false,
		label = '',
		required = false,
		optional = false,
		error = '',
		name,
		value = $bindable(undefined),
		options,
		searchable = false,
		searchPlaceholder = 'Search...',
		placeholder = '',
		ariaLabel,
		selectedPrefix = undefined,
		dataCy,
		tooltip = undefined,
		classes = {},
		showIcon = true,
		children,
		icon,
		onblur,
		onchange,
		optionSlot,
		selectedOptionSlot,
		...rest
	}: SelectProps = $props();

	let buttonElement: HTMLButtonElement | undefined = $state();
	let menuElement: HTMLDivElement | undefined = $state();
	let searchInputElement: HTMLInputElement | undefined = $state();
	let open = $state(false);
	let highlightedIndex = $state(-1);
	let openedAbove = $state(false);
	let searchQuery = $state('');

	let dataCyPrefix = $derived(`${dataCy}-select`);

	let displayedOptions = $derived(
		searchable && searchQuery.trim()
			? options.filter((o) =>
					(o.label ?? '').toLowerCase().includes(searchQuery.trim().toLowerCase())
				)
			: options
	);

	// classes.menu may be a static string or a function of the open direction (so callers
	// can, e.g., reverse a drop shadow when the menu opens above the trigger).
	let menuClass = $derived(
		typeof classes.menu === 'function' ? classes.menu({ openedAbove }) : classes.menu
	);

	// Decide whether to open above the trigger: only when there isn't room below but there
	// is above. A one-time measurement at open — the menu is an in-flow absolute element,
	// so it moves with the trigger (page scroll, iOS keyboard) without any live listeners.
	const measureOpenDirection = () => {
		if (!buttonElement) return;
		const rect = buttonElement.getBoundingClientRect();
		const menuHeight = menuElement?.offsetHeight ?? 0;
		const spaceBelow = window.innerHeight - rect.bottom;
		openedAbove = spaceBelow < menuHeight && rect.top > spaceBelow;
	};

	$effect(() => {
		if (!open || !menuElement) return;
		// Measure once the menu has rendered so its height is known, then focus the search
		// input. requestAnimationFrame lets layout settle before we read heights.
		const raf = requestAnimationFrame(() => {
			measureOpenDirection();
			if (searchable) searchInputElement?.focus();
		});
		return () => cancelAnimationFrame(raf);
	});

	$effect(() => {
		// While searching, keep the first match highlighted so Enter selects it.
		if (searchable && open) {
			void searchQuery;
			highlightedIndex = displayedOptions.length ? 0 : -1;
		}
	});

	const closeDropdown = () => {
		if (!open) return;
		open = false;
		highlightedIndex = -1;
		searchQuery = '';
		openedAbove = false;
		onblur?.();
	};

	const updateValue = (option: SelectOption) => {
		const event: SelectChangeEvent['detail'] = {
			value: option.value as string,
			selectedOption: option
		};

		onchange?.({ detail: event } as SelectChangeEvent);
		value = option.value as string;
	};

	const selectOption = (option: SelectOption) => {
		updateValue(option);
		open = false;
		highlightedIndex = -1;
		searchQuery = '';
		onblur?.();
		buttonElement?.focus();
	};

	const toggleOpen = () => {
		if (disabled) return;
		if (open) {
			closeDropdown();
		} else {
			open = true;
			highlightedIndex = getSelectedIndex();
		}
	};

	const getSelectedIndex = () => displayedOptions.findIndex((o) => o.value === value);

	const selectByOffset = (offset: number) => {
		const currentIndex = getSelectedIndex();
		const nextIndex = Math.max(0, Math.min(options.length - 1, currentIndex + offset));
		if (nextIndex !== currentIndex || currentIndex === -1) {
			updateValue(options[nextIndex]);
		}
	};

	const handleTriggerKeydown = (e: KeyboardEvent) => {
		switch (e.key) {
			case 'ArrowDown':
				e.preventDefault();
				if (!open) {
					selectByOffset(1);
				} else if (displayedOptions.length) {
					highlightedIndex = (highlightedIndex + 1) % displayedOptions.length;
					if (!searchable) updateValue(displayedOptions[highlightedIndex]);
				}
				break;
			case 'ArrowUp':
				e.preventDefault();
				if (!open) {
					selectByOffset(-1);
				} else if (displayedOptions.length) {
					highlightedIndex =
						(highlightedIndex - 1 + displayedOptions.length) % displayedOptions.length;
					if (!searchable) updateValue(displayedOptions[highlightedIndex]);
				}
				break;
			case 'Enter':
			case ' ':
				// A space is a literal character in the search box, not a select action.
				if (searchable && open && e.key === ' ') break;
				e.preventDefault();
				if (open && highlightedIndex >= 0 && highlightedIndex < displayedOptions.length) {
					selectOption(displayedOptions[highlightedIndex]);
				} else {
					toggleOpen();
				}
				break;
			case 'Escape':
				if (open) {
					e.preventDefault();
					closeDropdown();
				}
				break;
			case 'Tab':
				if (open) {
					closeDropdown();
				}
				break;
		}
	};

	let selectedOption = $derived(options?.find((option) => option.value === value));
</script>

<div
	class={twMerge('relative flex flex-col', rest.class)}
	use:clickOutside={`[data-select-menu="${dataCyPrefix}"]`}
	onclick_outside={() => closeDropdown()}
>
	{#if label}
		<div class={twMerge('flex justify-between mb-2')}>
			<InputLabel
				variant={labelVariant}
				dataCy={dataCyPrefix}
				{name}
				{required}
				{optional}
				class={classes.label}
			>
				{label}
			</InputLabel>

			{#if tooltip}
				<InfoTooltip
					dataCy={dataCyPrefix}
					placement="top-end"
					content={tooltip}
					classes={classes?.tooltipClasses}
					class={classes?.tooltip}
				/>
			{/if}
		</div>
	{/if}

	<!-- Positioning context for the menu: anchor it to the button, not the whole wrapper
	(which includes the label above), so opening upward doesn't leave a label-sized gap. -->
	<div class="relative w-full">
		<button
			bind:this={buttonElement}
			data-cy={dataCyPrefix}
			type="button"
			class={twMerge(
				'font-silka flex h-[2.5rem] w-full items-center justify-between gap-1.5 rounded border bg-white px-[0.625rem] text-[0.875rem] leading-[1rem] transition-all',
				classNames({
					'h-[2rem] text-[0.75rem]': size === 'sm',
					'hover:border-gray-800 hover:opacity-90': !disabled,
					'text-gray-400': (!selectedOption || disabled) && !isIosDevice(),
					'text-gray-900': (!selectedOption || disabled) && isIosDevice(),
					'border-gray-200': !error,
					'bg-gray-50': disabled,
					'border-red-500': error
				}),
				classes.button
			)}
			id={name}
			{disabled}
			aria-label={ariaLabel}
			aria-expanded={open}
			aria-haspopup="listbox"
			onclick={toggleOpen}
			onkeydown={handleTriggerKeydown}
		>
			{#if selectedOptionSlot}
				{@render selectedOptionSlot?.(selectedOption)}
			{:else}
				{#if selectedOption?.image}
					<img
						alt={selectedOption?.image?.alt}
						src={selectedOption?.image?.url}
						width={selectedOption?.image?.width}
						height={selectedOption?.image?.height}
					/>
				{/if}
				<Txt
					variant="label3"
					class={twMerge(
						classNames('overflow-hidden text-ellipsis whitespace-nowrap text-left font-normal', {
							'text-[0.75rem]': size === 'sm',
							'text-gray-400': !value || value === NO_VALUE,
							'min-w-[calc(100%-1.625rem)] max-w-[calc(100%-1.625rem)]':
								showIcon && !selectedOption?.image,
							'min-w-[calc(100%-6.625rem)] max-w-[calc(100%-6.625rem)]':
								showIcon && selectedOption?.image
						}),
						classes.placeholder
					)}
				>
					{`${selectedPrefix ? `${selectedPrefix} ` : ''}${
						value ? selectedOption?.label : placeholder
					}`}
				</Txt>
			{/if}

			{#if showIcon}
				<ChevronDownIcon
					class={size === 'sm'
						? 'max-h-[1rem] min-h-[1rem] min-w-[1rem] max-w-[1rem]'
						: 'max-h-[1.25rem] min-h-[1.25rem] min-w-[1.25rem] max-w-[1.25rem]'}
					{...disabled && { color: 'gray-400' }}
				/>
			{/if}
			{@render icon?.()}
		</button>

		{#if open}
			<div
				bind:this={menuElement}
				data-select-menu={dataCyPrefix}
				class={twMerge(
					'bg-gray-0 absolute left-0 right-0 z-[100] flex max-h-[300px] flex-col overflow-hidden rounded shadow focus:!ring-0',
					openedAbove ? 'bottom-full mb-1' : 'top-full mt-1',
					menuClass
				)}
				transition:fade={{ duration: 150 }}
			>
				{#if searchable && !openedAbove}
					{@render searchInput()}
				{/if}
				<div class="flex flex-col overflow-y-auto" role="listbox" aria-label={ariaLabel}>
					{#if searchable && !displayedOptions.length}
						<Txt
							variant="label3"
							dataCy={`${dataCyPrefix}-no-results`}
							class="font-silka p-3 font-normal text-gray-400"
						>
							No results
						</Txt>
					{/if}
					{#each displayedOptions as option, i}
						{#if !optionSlot}
							<div
								data-label={option.label}
								data-value={option.value}
								data-cy={`${dataCyPrefix}-option`}
								class={twMerge(
									'bg-white font-silka focus:text-magnum-700 relative flex cursor-pointer items-center gap-4 rounded p-3 text-neutral-800 hover:bg-gray-100 focus:z-20',
									classNames({
										'py-1.5': size === 'sm',
										'bg-gray-100': highlightedIndex === i
									}),
									classes.option
								)}
								role="option"
								tabindex="-1"
								aria-selected={option.value === value}
								onclick={() => selectOption(option)}
								onkeydown={(e: { key: string }) => {
									if (e.key === 'Enter') selectOption(option);
								}}
							>
								{#if option.image}
									<img
										alt={option?.image?.alt}
										src={option?.image?.url}
										width={option?.image?.width}
										height={option?.image?.height}
									/>
								{/if}
								<Txt
									variant="label3"
									class={twMerge(
										'color-gray-900 min-w-0 overflow-hidden text-ellipsis whitespace-nowrap font-normal',
										classNames({
											'text-[0.75rem]': size === 'sm',
											'text-gray-400': option.value === NO_VALUE
										})
									)}
								>
									{option.label}
								</Txt>
							</div>
						{:else}
							<div
								data-label={option.label}
								data-value={option.value}
								data-cy={`${dataCyPrefix}-option`}
								class={twMerge(
									'font-silka focus:text-magnum-700 relative cursor-pointer rounded p-3 text-neutral-800 focus:z-20',
									classNames({
										'bg-gray-100': highlightedIndex === i
									}),
									classes.option
								)}
								role="option"
								tabindex="-1"
								aria-selected={option.value === value}
								onclick={() => selectOption(option)}
								onkeydown={(e: { key: string }) => {
									if (e.key === 'Enter') selectOption(option);
								}}
							>
								{@render optionSlot?.(option)}
							</div>
						{/if}
					{/each}
				</div>
				{#if searchable && openedAbove}
					{@render searchInput()}
				{/if}
			</div>
		{/if}
	</div>
</div>

{#snippet searchInput()}
	<div
		class={twMerge(
			'bg-white p-2',
			openedAbove ? 'border-t border-gray-100' : 'border-b border-gray-100'
		)}
	>
		<input
			bind:this={searchInputElement}
			bind:value={searchQuery}
			data-cy={`${dataCyPrefix}-search`}
			type="text"
			class="font-silka h-[2rem] w-full rounded border border-gray-200 px-2 text-[0.875rem] leading-[1rem] text-gray-900 focus:border-gray-800 focus:outline-none"
			placeholder={searchPlaceholder}
			aria-label={searchPlaceholder}
			autocomplete="off"
			onkeydown={handleTriggerKeydown}
		/>
	</div>
{/snippet}

{#if error}
	<Txt dataCy={`${dataCyPrefix}-error`} variant="label4" class={'mt-1 text-red-500'}>
		{error}
	</Txt>
{/if}

{#if children}
	<Txt
		dataCy={`${dataCyPrefix}-helper`}
		variant="label4"
		class={twMerge('mt-1 text-gray-500', classes?.helper)}
	>
		{@render children?.()}
	</Txt>
{/if}
